import { useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '../../utils/api.js';
import { locationHooks, meHooks, customerHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import { GP_TYPE_LABELS } from '../../utils/constants.js';

async function downloadExcel(endpoint, params, filename) {
  const res = await api.get(endpoint, { params, responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const { data: locations = [] } = locationHooks.useList();
  const { data: mes = [] } = meHooks.useList();
  const { data: customers = [] } = customerHooks.useList();

  const [gpFilters, setGPFilters] = useState({ type: '', status: '', locationId: '', dateFrom: '', dateTo: '' });
  const [iceLocId, setIceLocId] = useState('');
  const [iceFrom, setIceFrom] = useState('');
  const [iceTo, setIceTo] = useState('');
  const [iceCustId, setIceCustId] = useState('');
  const [meId, setMeId] = useState('');
  const [meDateFrom, setMeDateFrom] = useState('');
  const [meDateTo, setMeDateTo] = useState('');
  const [loading, setLoading] = useState('');

  const dl = async (endpoint, params, name) => {
    setLoading(name);
    try { await downloadExcel(endpoint, params, name + '.xlsx'); } finally { setLoading(''); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500 mt-0.5">Export data to Excel / CSV</p>
      </div>

      {/* GP Register */}
      <Card title="Gate Pass Register">
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select value={gpFilters.type} onChange={e => setGPFilters(f => ({ ...f, type: e.target.value }))}>
              <option value="">All types</option>
              {Object.entries(GP_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <Select value={gpFilters.locationId} onChange={e => setGPFilters(f => ({ ...f, locationId: e.target.value }))}>
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
            </Select>
            <Input type="date" value={gpFilters.dateFrom} onChange={e => setGPFilters(f => ({ ...f, dateFrom: e.target.value }))} placeholder="From" />
            <Input type="date" value={gpFilters.dateTo} onChange={e => setGPFilters(f => ({ ...f, dateTo: e.target.value }))} placeholder="To" />
          </div>
          <Button icon={Download} loading={loading === 'GP-Register'} onClick={() => dl('/reports/gp-register', gpFilters, 'GP-Register')}>
            Download Gate Pass Register
          </Button>
        </div>
      </Card>

      {/* ME Ledger */}
      <Card title="Merchant Exporter Material Ledger">
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Select value={meId} onChange={e => setMeId(e.target.value)}>
              <option value="">Select ME…</option>
              {mes.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
            <Input type="date" value={meDateFrom} onChange={e => setMeDateFrom(e.target.value)} />
            <Input type="date" value={meDateTo} onChange={e => setMeDateTo(e.target.value)} />
          </div>
          <Button icon={Download} disabled={!meId} loading={loading === 'ME-Ledger'} onClick={() => dl('/reports/me-ledger', { merchantExporterId: meId, dateFrom: meDateFrom, dateTo: meDateTo }, 'ME-Ledger')}>
            Download ME Ledger
          </Button>
        </div>
      </Card>

      {/* Pending Returns */}
      <Card title="Pending Returns Report">
        <div className="p-6">
          <p className="text-sm text-gray-500 mb-4">All returnable gate passes currently in DISPATCHED status</p>
          <Button icon={Download} loading={loading === 'Pending-Returns'} onClick={() => dl('/reports/pending-returns', {}, 'Pending-Returns')}>
            Download Pending Returns
          </Button>
        </div>
      </Card>

      {/* Ice Sales Report */}
      <Card title="Ice Sales Report">
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">Ice sold to external customers — filter by period and customer</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select value={iceLocId} onChange={e => setIceLocId(e.target.value)}>
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
            </Select>
            <Select value={iceCustId} onChange={e => setIceCustId(e.target.value)}>
              <option value="">All customers</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Input type="date" value={iceFrom} onChange={e => setIceFrom(e.target.value)} placeholder="From" />
            <Input type="date" value={iceTo} onChange={e => setIceTo(e.target.value)} placeholder="To" />
          </div>
          <Button icon={Download} loading={loading === 'Ice-Sales'} onClick={() => dl('/reports/ice-sales', { locationId: iceLocId, customerId: iceCustId, dateFrom: iceFrom, dateTo: iceTo }, 'Ice-Sales')}>
            Download Ice Sales Report
          </Button>
        </div>
      </Card>

      {/* Ice Own Consumption Report */}
      <Card title="Ice Own Consumption Report">
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">Ice used for own consumption across locations — filter by period</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Select value={iceLocId} onChange={e => setIceLocId(e.target.value)}>
              <option value="">All locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.code}</option>)}
            </Select>
            <Input type="date" value={iceFrom} onChange={e => setIceFrom(e.target.value)} placeholder="From" />
            <Input type="date" value={iceTo} onChange={e => setIceTo(e.target.value)} placeholder="To" />
          </div>
          <Button icon={Download} loading={loading === 'Ice-Own'} onClick={() => dl('/reports/ice-own-consumption', { locationId: iceLocId, dateFrom: iceFrom, dateTo: iceTo }, 'Ice-Own-Consumption')}>
            Download Ice Consumption Report
          </Button>
        </div>
      </Card>
    </div>
  );
}
